import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface HealthCheckData {
  serviceName: string;
  status: string;
  latency: number;
  timestamp: string;
  message: string;
}

interface HistoricalData {
  serviceName: string;
  period: string;
  checks: number;
  data: HealthCheckData[];
}

const baseUrl = process.env.REACT_APP_API_URL;

const SERVICES = ['auth', 'psp', 'exchangeRateEngine', 'settlementEngine'];

export default function HistoricalCharts() {
  const [selectedService, setSelectedService] = useState<string>('psp');
  const [timeRange, setTimeRange] = useState<number>(1); // hours
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchHistoricalData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${baseUrl}/health-checker/history/${selectedService}?hours=${timeRange}`
        );
        const data: HistoricalData = await response.json();

        // Determine sampling interval based on time range
        let samplingInterval = 1;
        if (timeRange === 1) {
          samplingInterval = 5; // 1 hora: mostrar 1 de cada 5 puntos
        } else if (timeRange === 6) {
          samplingInterval = 15; // 6 horas: mostrar 1 de cada 15 puntos
        } else if (timeRange === 24) {
          samplingInterval = 30; // 24 horas: mostrar 1 de cada 30 puntos
        }

        // Transform data for recharts con sampling
        const transformed = data.data
          .filter((_, index) => index % samplingInterval === 0) // Aplicar sampling
          .map((check) => {
            const date = new Date(check.timestamp);
            return {
              time: date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              }),
              latency: check.latency,
              status: check.status,
              fullTime: date.toLocaleString('en-US', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })
            };
          });

        setChartData(transformed);
        setLoading(false);
      } catch (e) {
        console.error('Error fetching historical data:', e);
        setLoading(false);
      }
    };

    fetchHistoricalData();
    const interval = setInterval(fetchHistoricalData, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [selectedService, timeRange]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#FFFFFF',
          padding: '16px',
          border: '2px solid #C4A0D0',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(91, 21, 111, 0.15)'
        }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', fontWeight: '600' }}>
            {payload[0].payload.fullTime}
          </p>
          <p style={{ margin: '8px 0 0 0', color: '#5B156F', fontSize: '1.1rem', fontWeight: '700' }}>
            {payload[0].value}ms
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#64748b' }}>
            Status: <strong>{payload[0].payload.status}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px', width: '100%', boxSizing: 'border-box' }}>
      <div className="app-header">
        <h1>Historical Analysis</h1>
        <p>Latency and performance trends</p>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '16px', 
        marginBottom: '40px', 
        flexWrap: 'wrap',
        background: '#FFFFFF',
        padding: '24px',
        borderRadius: '16px',
        border: '2px solid #E8E4EC'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ 
            fontSize: '0.75rem', 
            fontWeight: '600', 
            color: '#94a3b8', 
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Service
          </label>
          <select 
            value={selectedService} 
            onChange={(e) => setSelectedService(e.target.value)}
            style={{ 
              padding: '12px 16px', 
              borderRadius: '12px', 
              border: '2px solid #E8E4EC',
              background: '#F8F6FA',
              fontSize: '0.95rem',
              fontWeight: '600',
              color: '#475569',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '200px'
            }}
          >
            {SERVICES.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ 
            fontSize: '0.75rem', 
            fontWeight: '600', 
            color: '#94a3b8', 
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Period
          </label>
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(Number(e.target.value))}
            style={{ 
              padding: '12px 16px', 
              borderRadius: '12px', 
              border: '2px solid #E8E4EC',
              background: '#F8F6FA',
              fontSize: '0.95rem',
              fontWeight: '600',
              color: '#475569',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '200px'
            }}
          >
            <option value={1}>Last hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>Loading historical data...</p>
        </div>
      ) : chartData.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: '#FFFFFF',
          borderRadius: '16px',
          border: '2px solid #E8E4EC'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📊</div>
          <p style={{ color: '#64748b', fontSize: '1.1rem', fontWeight: '600', marginBottom: '10px' }}>
            No data available
          </p>
          <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
            Make sure the service is running and saving data.
          </p>
        </div>
      ) : (
        <>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '16px',
            padding: '30px',
            border: '2px solid #E8E4EC'
          }}>
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4EC" />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  stroke="#cbd5e1"
                  interval="preserveStartEnd"
                />
                <YAxis 
                  label={{ value: 'Latency (ms)', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 12 }}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  stroke="#cbd5e1"
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="latency" 
                  stroke="#5B156F"
                  strokeWidth={2.5}
                  dot={{ r: 5, fill: '#5B156F', strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, fill: '#8B4F9F', strokeWidth: 2, stroke: '#fff' }}
                  name="Latency"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={{ 
            marginTop: '32px', 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              background: '#E8F7F4',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #98D8C8'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Average
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0D5A4F' }}>
                {(chartData.reduce((sum, d) => sum + d.latency, 0) / chartData.length).toFixed(0)}
                <span style={{ fontSize: '1.2rem', fontWeight: '500', marginLeft: '4px' }}>ms</span>
              </div>
            </div>
            <div style={{
              background: '#E8F7F4',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #98D8C8'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Minimum
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0D5A4F' }}>
                {Math.min(...chartData.map(d => d.latency))}
                <span style={{ fontSize: '1.2rem', fontWeight: '500', marginLeft: '4px' }}>ms</span>
              </div>
            </div>
            <div style={{
              background: '#FDE8F0',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #F7B5CA'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Maximum
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#A8154A' }}>
                {Math.max(...chartData.map(d => d.latency))}
                <span style={{ fontSize: '1.2rem', fontWeight: '500', marginLeft: '4px' }}>ms</span>
              </div>
            </div>
            <div style={{
              background: '#F8F6FA',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #C4A0D0'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Data Points
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#5B156F' }}>
                {chartData.length}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

